import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Input, Flex, Typography, Tabs, Alert, Spin } from 'antd';
import {
  IdcardOutlined,
  MobileOutlined,
  CheckCircleOutlined,
  UserOutlined,
  DownloadOutlined,
  PrinterOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import * as abhaApi from '../services/api';
import { 
  extractTxnId, 
  findToken, 
  findProfileData, 
  DataAbhaCard, 
  OtpInput, 
} from '../components/AbhaFlowComponents';
import miruLogo from '../assets/miru-logo.png';

const { Title, Paragraph, Text } = Typography;

const RESEND_SECONDS = 90;

const GenericFlow = ({ config, onDone, method }) => {
  const [step, setStep] = useState(0);
  const [inputVal, setInputVal] = useState('');
  const [otp, setOtp] = useState('');
  const [txnId, setTxnId] = useState('');
  const [loading, setLoading] = useState(false);
  const [showProfileFoundAlert, setShowProfileFoundAlert] = useState(false);
  const [profile, setProfile] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [error, setError] = useState(null);
  const [resendTimer, setResendTimer] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const timerRef = useRef(null);

  const startResendTimer = () => {
    setResendTimer(RESEND_SECONDS);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => () => clearInterval(timerRef.current), []);

  const handleResendOtp = async () => {
    setResendLoading(true);
    setError(null);
    try {
      const res = await config.generateOtp(inputVal);
      const nextId = extractTxnId(res);
      if (nextId) setTxnId(nextId);
      setOtp('');
      startResendTimer();
    } catch (err) {
      setError(err.message || 'Failed to resend OTP');
    } finally {
      setResendLoading(false);
    }
  };

  const handleViewCard = () => {
    setShowProfileFoundAlert(false);
    onDone(profile, txnId, method);
  };

  const updateTxnId = (res) => {
    const nextId = extractTxnId(res);
    if (nextId) setTxnId(nextId);
  };

  const handleAction = async () => {
    setError(null);
    setLoading(true);
    try {
      if (step === 0) {
        const res = await config.generateOtp(inputVal);
        updateTxnId(res);
        setStep(1);
        startResendTimer();
      } else {
        const res = await config.verifyOtp(otp, txnId);

        const foundAccounts = 
          res.accounts || 
          res.data?.accounts || 
          res.users || 
          res.finalResponse?.users ||
          (Array.isArray(res) ? res : (Array.isArray(res.data) ? res.data : null));

        if (foundAccounts && Array.isArray(foundAccounts)) {
          const rootToken = res.token || findToken(res) || txnId;
          
          if (foundAccounts.length === 1) {
            const prof = foundAccounts[0];
            const accToken = findToken(prof) || rootToken;
            // Merge with findProfileData results to ensure all fields are captured
            const mergedProfile = findProfileData(res);
            setProfile({ ...(mergedProfile || prof), token: accToken });
            setShowProfileFoundAlert(true);
          } else if (foundAccounts.length > 1) {
            const accountsWithToken = foundAccounts.map(acc => {
              const accToken = findToken(acc) || rootToken;
              return { ...acc, token: accToken };
            });
            setAccounts(accountsWithToken);
            setStep(2);
          } else {
            throw new Error('No ABHA accounts found.');
          }
        } else {
          const profileData = findProfileData(res);
          if (!profileData) {
            const errorFromData = res?.data?.message || res?.data?.error;
            const errorFromRoot = res?.message || res?.error;
            const errMsg = errorFromData || errorFromRoot || 'Profile not found. Please verify your OTP and try again.';
            throw new Error(errMsg);
          }
          if (!findToken(profileData) && txnId) {
            profileData.token = txnId;
          }
          setProfile(profileData);
          setShowProfileFoundAlert(true);
        }
      }
    } catch (err) {
      setError(err.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const selectAccount = (acc) => {
    onDone(acc, txnId, method);
  };

  return (
    <Spin spinning={loading}>
      <div style={{ minHeight: 140 }}>
        {error && <Alert message={error} type="error" showIcon closable className="mb-4" />}
        {step === 0 && (
          <div className="py-4">
            <Text strong className="block mb-2">{config.inputLabel}</Text>
            <Input 
              size="large" 
              placeholder={config.inputPlaceholder} 
              value={inputVal} 
              onChange={e => setInputVal(e.target.value)} 
              prefix={config.inputPrefix} 
              style={{ borderRadius: 10 }}
            />
          </div>
        )}
        {step === 1 && (
          <div className="text-center py-4">
            {showProfileFoundAlert && (
              <Alert
                message="ABHA profile exists"
                description="We found an ABHA profile associated with these details."
                type="success"
                showIcon
                action={
                  <Button size="small" type="primary" onClick={handleViewCard} style={{ background: '#6B35D4', borderColor: '#6B35D4' }}>
                    View ABHA Card
                  </Button>
                }
                className="mb-6"
              />
            )}
            <Paragraph>{config.verifyLabel}</Paragraph>
            <OtpInput value={otp} onChange={setOtp} />
            <div className="mt-4">
              {resendTimer > 0 ? (
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Resend OTP in{' '}
                  <Text strong style={{ color: '#6B35D4', fontSize: 13 }}>
                    {resendTimer}s
                  </Text>
                </Text>
              ) : (
                <Button
                  type="link"
                  size="small"
                  loading={resendLoading}
                  onClick={handleResendOtp}
                  style={{ color: '#6B35D4', fontWeight: 600, padding: 0 }}
                >
                  Resend OTP
                </Button>
              )}
            </div>
          </div>
        )}
        {step === 2 && (
          <div className="py-4">
            <Title level={5} className="mb-4">Multiple Accounts Found</Title>
            <Paragraph type="secondary">Please select the account you want to view:</Paragraph>
            <Flex vertical gap={12}>
              {accounts.map((acc, idx) => (
                <div 
                  key={idx} 
                  onClick={() => selectAccount(acc)}
                  style={{ 
                    padding: '16px', 
                    border: '1px solid #e2e8f0', 
                    borderRadius: '12px', 
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  className="account-select-card"
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#6B35D4'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
                >
                  <Flex justify="space-between" align="center">
                    <div>
                      <div style={{ fontWeight: 'bold' }}>{acc.name || `${acc.firstName} ${acc.lastName}`}</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{acc.ABHANumber || acc.healthIdNumber}</div>
                    </div>
                    <CheckCircleOutlined style={{ color: '#6B35D4' }} />
                  </Flex>
                </div>
              ))}
            </Flex>
          </div>
        )}
      </div>
      <Flex justify="space-between" align="center" className="mt-8">
        <div>
          {step > 0 && step < 2 && <Button onClick={() => { setStep(0); setShowProfileFoundAlert(false); }} disabled={loading} type="text">Back</Button>}
          {step === 2 && <Button onClick={() => setStep(1)} disabled={loading} type="text">Back to OTP</Button>}
        </div>
        {step < 2 && !showProfileFoundAlert && (
          <Button 
            type="primary" 
            loading={loading} 
            onClick={handleAction} 
            size="large"
            style={{ 
              background: '#6B35D4', 
              borderColor: '#6B35D4',
              borderRadius: 12,
              paddingInline: 32,
              height: 48,
              fontWeight: 600
            }}
          >
            {step === 0 ? 'Send OTP' : 'Verify & Fetch'}
          </Button>
        )}
      </Flex>
    </Spin>
  );
};

const FETCH_TABS = [
  {
    key: 'mobile', label: 'Mobile',
    config: {
      inputLabel: 'Mobile Number', inputPlaceholder: 'Enter registered mobile',
      inputPrefix: <MobileOutlined style={{ color: '#6B35D4' }} />,
      verifyLabel: 'Enter OTP sent to your mobile',
      generateOtp: abhaApi.generateForgetMobileOtp, 
      verifyOtp: abhaApi.verifyForgetMobileOtp,
    },
  },
  {
    key: 'address', label: 'ABHA Address',
    config: {
      inputLabel: 'ABHA Address', inputPlaceholder: 'username@sbx',
      inputPrefix: <UserOutlined style={{ color: '#6B35D4' }} />,
      verifyLabel: 'Enter OTP sent to your registered mobile',
      generateOtp: abhaApi.generateAbhaAddressOtp, 
      verifyOtp: abhaApi.verifyAbhaAddressOtp,
    },
  },
  {
    key: 'number', label: 'ABHA Number',
    config: {
      inputLabel: 'ABHA Number', inputPlaceholder: '91-XXXX-XXXX-XXXX',
      inputPrefix: <IdcardOutlined style={{ color: '#6B35D4' }} />,
      verifyLabel: 'Enter OTP sent to your registered mobile',
      generateOtp: (val) => abhaApi.generateAuthOtp(val), 
      verifyOtp: abhaApi.verifyAuthOtp,
    },
  },
];

const FetchAbhaPage = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [txnId, setTxnId] = useState('');
  const [done, setDone] = useState(false);
  const [fetchMethod, setFetchMethod] = useState('');

  const handleFlowDone = (prof, tid, method) => {
    setProfile(prof);
    setTxnId(tid);
    setFetchMethod(method);
    setDone(true);
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '40px 20px' }}>
      <div style={{ maxWidth: 500, margin: '0 auto', background: '#fff', borderRadius: 24, boxShadow: '0 10px 40px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
        <div style={{ padding: '24px 32px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }}>
          <img 
            src={miruLogo} 
            alt="MiRu" 
            style={{ height: 60, marginBottom: 16, cursor: 'pointer' }} 
            onClick={() => navigate('/')}
          />
          {/* <div className="inline-block px-2 py-0.5 rounded bg-purple-50 text-purple-700 text-[10px] font-bold mb-1">ABDM SANDBOX</div> */}
          <Title level={3} style={{ margin: 0 }}>Find ABHA ID</Title>
        </div>

        <div style={{ padding: '32px' }}>
          {done ? (
            <div className="py-4">
              <div className="text-center mb-6">
                <CheckCircleOutlined style={{ fontSize: 48, color: '#12B76A' }} />
                <Title level={3} style={{ marginTop: 12 }}>ABHA Profile Found!</Title>
              </div>
              
              <DataAbhaCard profile={profile} hideGenderDob={fetchMethod === 'number'} />

              <Flex vertical gap={12} className="mt-6">
                <Button 
                  type="primary"
                  size="large"
                  style={{ borderRadius: 12, background: '#6B35D4', borderColor: '#6B35D4' }}
                  onClick={() => { setDone(false); setProfile(null); }}
                >
                  Find Another
                </Button>
                <Button 
                  size="large" 
                  variant="text"
                  onClick={() => navigate('/')}
                >
                  Back to Home
                </Button>
              </Flex>
            </div>
          ) : (
            <div>
              <Tabs 
                defaultActiveKey="mobile" 
                centered
                items={FETCH_TABS.map(t => ({ 
                  key: t.key, 
                  label: t.label, 
                  children: <GenericFlow config={t.config} onDone={handleFlowDone} method={t.key} /> 
                }))} 
              />
              
              <div className="mt-10 flex justify-center">
                <Button 
                  icon={<ArrowLeftOutlined />}
                  onClick={() => navigate('/')}
                  type="text"
                >
                  Back to Home
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FetchAbhaPage;
